import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getActivePlans } from '../services/pricingPlanService';
import type { PricingPlan } from '../services/pricingPlanService';
import { SEO } from '../components/SEO';
import PublicNav from '../components/PublicNav';

/**
 * Pricing Page Component
 *
 * Public-facing pricing page for Nivoon with JIRA-style tier layout.
 * Fetches plans dynamically from the API. Falls back to empty state on error.
 * Matches the visual style of the Landing page.
 */

interface DisplayTier {
  name: string;
  description: string | null;
  price: number | null;
  priceAnnual: number | null;
  cta: string;
  ctaStyle: string;
  highlighted: boolean;
  badge: string | null;
  features: string[];
  notIncluded: string[];
}

function mapPlanToTier(plan: PricingPlan, isHighlighted: boolean): DisplayTier {
  let features: string[] = [];
  let notIncluded: string[] = [];
  try { features = JSON.parse(plan.features || '[]'); } catch { /* empty */ }
  try { notIncluded = JSON.parse(plan.not_included || '[]'); } catch { /* empty */ }

  return {
    name: plan.name,
    description: plan.description,
    price: plan.price_monthly,
    priceAnnual: plan.price_annual,
    cta: plan.cta_text || 'Get Started',
    ctaStyle: isHighlighted
      ? 'bg-gradient-to-r from-indigo-600 to-blue-600 text-white hover:from-indigo-700 hover:to-blue-700 shadow-lg'
      : 'border-2 border-gray-300 text-gray-700 hover:border-indigo-600 hover:text-indigo-600',
    highlighted: isHighlighted,
    badge: plan.highlight_badge,
    features,
    notIncluded
  };
}

export function Pricing(): React.ReactElement {
  const navigate = useNavigate();
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'annual'>('monthly');
  const [tiers, setTiers] = useState<DisplayTier[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadPlans = async () => {
      try {
        const plans = await getActivePlans();
        const mapped = plans.map(plan => mapPlanToTier(plan, plan.is_highlighted));
        setTiers(mapped);
      } catch (err) {
        console.error('Failed to load pricing plans:', err);
      } finally {
        setLoading(false);
      }
    };
    loadPlans();
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Pricing"
        description="Simple, transparent pricing for teams of any size. Start free with a 14-day trial. Monthly and annual plans with AI-powered requirements management."
        path="/pricing"
      />

      <PublicNav activePage="pricing" />

      {/* Hero Header */}
      <section className="pt-16 pb-8 bg-gradient-to-b from-slate-900 to-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Simple, transparent pricing
          </h1>
          <p className="text-xl text-gray-300 max-w-2xl mx-auto mb-8">
            Choose the plan that fits your team. Start free and scale as you grow.
          </p>

          {/* Billing Toggle */}
          <div className="inline-flex items-center bg-slate-700 rounded-full p-1">
            <button
              onClick={() => setBillingCycle('monthly')}
              className={`px-6 py-2 rounded-full text-sm font-medium transition-all ${
                billingCycle === 'monthly'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-300 hover:text-white'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingCycle('annual')}
              className={`px-6 py-2 rounded-full text-sm font-medium transition-all ${
                billingCycle === 'annual'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-300 hover:text-white'
              }`}
            >
              Annual
              <span className="ml-2 text-xs bg-green-500 text-white px-2 py-0.5 rounded-full">
                Save 20%
              </span>
            </button>
          </div>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
              <span className="ml-3 text-gray-500">Loading plans...</span>
            </div>
          ) : tiers.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500 text-lg">Pricing information is currently unavailable. Please try again later.</p>
            </div>
          ) : (
            <div className={`grid grid-cols-1 md:grid-cols-2 ${tiers.length >= 4 ? 'lg:grid-cols-4' : tiers.length === 3 ? 'lg:grid-cols-3' : ''} gap-6`}>
              {tiers.map((tier) => (
                <div
                  key={tier.name}
                  className={`relative bg-white rounded-2xl shadow-md flex flex-col ${
                    tier.highlighted
                      ? 'ring-2 ring-indigo-600 shadow-xl scale-[1.02]'
                      : 'border border-gray-200'
                  }`}
                >
                  {/* Badge */}
                  {tier.badge && (
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                      <span className="bg-gradient-to-r from-indigo-600 to-blue-600 text-white text-sm font-semibold px-4 py-1.5 rounded-full shadow-md">
                        {tier.badge}
                      </span>
                    </div>
                  )}

                  <div className="p-8 flex-1 flex flex-col">
                    {/* Tier Name */}
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{tier.name}</h3>
                    <p className="text-sm text-gray-500 mb-6">{tier.description}</p>

                    {/* Price */}
                    <div className="mb-8">
                      {tier.price !== null ? (
                        <div className="flex items-baseline">
                          <span className="text-5xl font-bold text-gray-900">
                            ${billingCycle === 'annual' ? tier.priceAnnual : tier.price}
                          </span>
                          <span className="text-gray-500 ml-2">/user/month</span>
                        </div>
                      ) : (
                        <div>
                          <span className="text-3xl font-bold text-gray-900">Custom</span>
                          <p className="text-sm text-gray-500 mt-1">Tailored to your needs</p>
                        </div>
                      )}
                      {billingCycle === 'annual' && tier.price !== null && (
                        <p className="text-sm text-gray-500 mt-1">Billed annually</p>
                      )}
                    </div>

                    {/* CTA Button */}
                    <button
                      onClick={() => navigate('/register')}
                      className={`w-full py-3 px-6 rounded-lg font-semibold transition-all mb-8 ${tier.ctaStyle}`}
                    >
                      {tier.cta}
                    </button>

                    {/* Feature List */}
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-gray-900 mb-4">What's included:</p>
                      <ul className="space-y-3">
                        {tier.features.map((feature, idx) => (
                          <li key={idx} className="flex items-start">
                            {feature.includes('Everything in') ? (
                              <>
                                <svg className="w-5 h-5 text-indigo-600 mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                                <span className="text-sm font-semibold text-indigo-600">{feature}</span>
                              </>
                            ) : (
                              <>
                                <svg className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                                <span className="text-sm text-gray-700">{feature}</span>
                              </>
                            )}
                          </li>
                        ))}
                      </ul>

                      {/* Not Included */}
                      {tier.notIncluded.length > 0 && (
                        <div className="mt-6 pt-4 border-t border-gray-100">
                          <ul className="space-y-3">
                            {tier.notIncluded.map((feature, idx) => (
                              <li key={idx} className="flex items-start">
                                <svg className="w-5 h-5 text-gray-300 mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                                <span className="text-sm text-gray-400">{feature}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
            Frequently asked questions
          </h2>
          <div className="space-y-6">
            {[
              {
                q: 'Can I switch plans at any time?',
                a: 'Yes, you can upgrade or downgrade your plan at any time. When upgrading, the new features are available immediately. When downgrading, changes take effect at the start of your next billing cycle.',
              },
              {
                q: 'Is there a free trial for paid plans?',
                a: 'Yes, all paid plans come with a 14-day free trial. No credit card required to start.',
              },
              {
                q: 'What happens when I exceed my user limit?',
                a: 'We\'ll notify you when you\'re approaching your plan\'s user limit. You can upgrade your plan at any time to add more users.',
              },
              {
                q: 'Do you offer discounts for nonprofits or education?',
                a: 'Yes, we offer special pricing for qualifying nonprofit organizations and educational institutions. Contact our sales team for details.',
              },
              {
                q: 'How does billing work?',
                a: 'We charge per user on your selected billing cycle (monthly or annual). Annual billing gives you a 20% discount compared to monthly billing.',
              },
            ].map((faq, idx) => (
              <div key={idx} className="bg-gray-50 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{faq.q}</h3>
                <p className="text-gray-600">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-indigo-600 to-blue-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Ready to transform your requirements process?
          </h2>
          <p className="text-xl text-indigo-100 mb-8">
            Start free and upgrade as your team grows. No credit card required.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => navigate('/register')}
              className="px-8 py-4 bg-white text-indigo-600 font-semibold rounded-lg hover:bg-gray-100 transition-all text-lg shadow-lg"
            >
              Get Started for Free
            </button>
            <button
              onClick={() => navigate('/register')}
              className="px-8 py-4 bg-indigo-700 text-white font-semibold rounded-lg border-2 border-white hover:bg-indigo-800 transition-all text-lg"
            >
              Contact Sales
            </button>
          </div>
        </div>
      </section>

      {/* Footer - matches Landing page */}
      <footer className="bg-gray-900 text-gray-300 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-8">
            <div>
              <h3 className="text-white font-semibold mb-4">Product</h3>
              <ul className="space-y-2">
                <li><a href="/features" className="hover:text-white transition-colors">Features</a></li>
                <li><span className="text-white">Pricing</span></li>
                <li><a href="/features#enterprise-security---administration" className="hover:text-white transition-colors">Security</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Roadmap</a></li>
              </ul>
            </div>
            <div>
              <h3 className="text-white font-semibold mb-4">Company</h3>
              <ul className="space-y-2">
                <li><a href="#" className="hover:text-white transition-colors">Blog</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Careers</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Contact</a></li>
                <li><a href="/investor-relations" className="hover:text-white transition-colors">Investor Relations</a></li>
              </ul>
            </div>
            <div>
              <h3 className="text-white font-semibold mb-4">Resources</h3>
              <ul className="space-y-2">
                <li><a href="#" className="hover:text-white transition-colors">Documentation</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Help Center</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Community</a></li>
                <li><a href="#" className="hover:text-white transition-colors">API</a></li>
              </ul>
            </div>
            <div>
              <h3 className="text-white font-semibold mb-4">Legal</h3>
              <ul className="space-y-2">
                <li><a href="/legal#privacy" className="hover:text-white transition-colors">Privacy</a></li>
                <li><a href="/legal#terms" className="hover:text-white transition-colors">Terms</a></li>
                <li><a href="/legal#cookie-policy" className="hover:text-white transition-colors">Cookie Policy</a></li>
                <li><a href="/legal#licenses" className="hover:text-white transition-colors">Licenses</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row justify-between items-center">
            <div className="mb-4 md:mb-0 cursor-pointer" onClick={() => navigate('/')}>
              <img src="/logo1.png" alt="Nivoon" className="h-8 opacity-70" />
            </div>
            <p className="text-sm text-gray-400">
              &copy; 2025 Nivoon. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default Pricing;
